import * as React from 'react';
import { useTranslation } from 'react-i18next';
import { createSearchParams, Link, To, useNavigate, useSearchParams } from 'react-router-dom';
import { faChevronRight, faPlus, faSliders } from '@fortawesome/pro-regular-svg-icons';
import Button from '../../../core/components/Button.tsx';
import { withIcon } from '../../../core/components/Icon.tsx';
import Loader from '../../../core/components/Loader.tsx';
import { SettingsCard } from '../../../core/components/SettingsFormCard.tsx';
import { DocumentType, mapDocumentTypeToGqlDocumentType } from '../../constants.ts';
import { useLlmExtractorOutputFieldNames } from '../../llmExtractorFields.ts';
import { GROUP_SEPARATOR } from '../../pages/Assistance/useDocumentConfig.ts';
import FieldsConfig, { FieldsConfigBreadcrumbParent } from '../FieldsConfig.tsx';
import { FieldDefinition } from '../fieldsConfigData.ts';
import RuleEditor from './RuleEditor.tsx';
import RuleRow from './RuleRow.tsx';
import { NEW_RULE_ID, RuleLevel, useRules } from './rules.ts';

const ChevronRightIcon = withIcon(faChevronRight);
const PlusIcon = withIcon(faPlus);
const SlidersIcon = withIcon(faSliders);

const VIEW_PARAM = 'fine-tuning';

interface UseFieldFineTuningProps {
    // undefined when the field's section or the product has no fine-tuning
    level?: RuleLevel;
    channelId?: string;
    documentType: DocumentType;
    channelFields: FieldDefinition[];
    // the open field's full name, null while no field is open
    field: string | null;
    fieldLabel: string;
    fieldPath: string;
    // the breadcrumb up to the field
    parents: FieldsConfigBreadcrumbParent[];
    // true while the open rule has unsaved edits, for the page's blocker
    hasChangesRef: React.RefObject<boolean>;
    getRuleUrl: (channelId: string, ruleId: string) => To;
}

interface FieldFineTuning {
    // the row on the field's page
    entry: React.ReactNode;
    // the rule list or a rule, shown instead of the field's page
    page: React.ReactNode;
}

// The rules that write to the open field or one of its subfields: a row on the field's page, and from there their
// list and each rule in the field's URL: ?fine-tuning= the list, =<ruleId> a rule, =new a new rule, =new&copy=<id> a
// copy
export const useFieldFineTuning = ({
    level,
    channelId,
    documentType,
    channelFields,
    field,
    fieldLabel,
    fieldPath,
    parents,
    hasChangesRef,
    getRuleUrl,
}: UseFieldFineTuningProps): FieldFineTuning => {
    const { t } = useTranslation('config');
    const navigate = useNavigate();
    const [searchParams] = useSearchParams();
    const gqlDocumentType = mapDocumentTypeToGqlDocumentType[documentType];
    const ruleLevel = level ?? 'header';
    const isAvailable = !!level && !!field && !!channelId;
    const { rules, loading, refetch } = useRules(ruleLevel, channelId, gqlDocumentType, !isAvailable);
    const outputs = useLlmExtractorOutputFieldNames(channelId, gqlDocumentType, ruleLevel, !isAvailable);

    if (!isAvailable) {
        return { entry: null, page: null };
    }

    const view = searchParams.get(VIEW_PARAM);
    const toView = (value: string | null, params: Record<string, string> = {}): To => ({
        pathname: fieldPath,
        search: value === null ? '' : createSearchParams({ [VIEW_PARAM]: value, ...params }).toString(),
    });
    const isWrittenTo = (outputField: string) =>
        outputField === field || outputField.startsWith(`${field}${GROUP_SEPARATOR}`);
    const fieldRules = rules.filter((rule) => rule.fields?.some(isWrittenTo));
    const title = t('configPage.fineTuning.title');

    const entry = (
        <Link
            to={toView('')}
            className="flex w-full cursor-pointer items-center gap-3 rounded-lg border border-solid border-line-weak bg-transparent px-4 py-3 text-left transition-colors hover:bg-surface-medium focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-line-brand-strong"
        >
            <SlidersIcon className="flex-none text-content-medium" />
            <span className="flex flex-1 flex-col">
                <span className="text-sm font-semibold text-content-strong">{title}</span>
                {!loading && (
                    <span className="text-sm text-content-medium">
                        {fieldRules.length
                            ? t('configPage.fineTuning.ruleCount', { count: fieldRules.length })
                            : t('configPage.fineTuning.field.noRules')}
                    </span>
                )}
            </span>
            <ChevronRightIcon className="flex-none text-content-medium" />
        </Link>
    );

    if (view === null) {
        return { entry, page: null };
    }

    if (loading) {
        return {
            entry,
            page: (
                <SettingsCard title={title}>
                    <Loader />
                </SettingsCard>
            ),
        };
    }

    const fieldParent = { label: fieldLabel, onClick: () => navigate(toView(null)) };
    const backToList = () => navigate(toView(''));

    if (view === NEW_RULE_ID || rules.some(({ id }) => id === view)) {
        return {
            entry,
            page: (
                <RuleEditor
                    level={ruleLevel}
                    channelId={channelId}
                    gqlDocumentType={gqlDocumentType}
                    channelFields={channelFields}
                    rules={rules}
                    ruleId={view}
                    copyOf={searchParams.get('copy')}
                    // the field and its subfields in use; a field with subfields isn't an output field itself
                    draftFields={outputs.filter(isWrittenTo)}
                    // from the field on, since five parts don't fit
                    parents={[fieldParent, { label: title, onClick: backToList }]}
                    openField={{ label: fieldLabel, isWrittenTo }}
                    hasChangesRef={hasChangesRef}
                    onBack={backToList}
                    onOpen={(ruleId) => navigate(toView(ruleId), { replace: true })}
                    onRefetch={refetch}
                    onCopy={() => navigate(toView(NEW_RULE_ID, { copy: view }))}
                    getRuleUrl={getRuleUrl}
                />
            ),
        };
    }

    return {
        entry,
        page: (
            <FieldsConfig.Page
                parents={[...parents, fieldParent]}
                current={title}
                title={title}
                subtitle={t('configPage.fineTuning.field.description')}
                actions={
                    <Button asChild variant="outline">
                        <Link to={toView(NEW_RULE_ID)}>
                            <PlusIcon /> {t('configPage.fineTuning.addRule')}
                        </Link>
                    </Button>
                }
            >
                {fieldRules.length > 0 ? (
                    <div className="flex flex-col gap-2">
                        {fieldRules.map((rule) => (
                            <RuleRow key={rule.id} rule={rule} to={toView(rule.id)} />
                        ))}
                    </div>
                ) : (
                    <p className="text-sm text-content-medium">{t('configPage.fineTuning.field.empty')}</p>
                )}
            </FieldsConfig.Page>
        ),
    };
};
